import { useState, useEffect, useCallback, useRef } from "react";
import { API, getConversations, getConversationThread, sendWhatsappMessage } from "../services/api";
import "./AdminMessages.css";

const POLL_INTERVAL_MS = 5000;
const MAX_TEXTAREA_HEIGHT = 140; // px, caps growth before it scrolls internally

function formatTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  return isToday
    ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

export default function AdminMessages() {
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [activePhone, setActivePhone] = useState(null);
  const [thread, setThread] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);
  const draftRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const r = await getConversations();
      setConversations(r.data.conversations || []);
    } catch {
      // keep last known list on transient failure
    }
  }, []);

  const loadThread = useCallback(async (phone) => {
    if (!phone) return;
    try {
      const r = await getConversationThread(phone);
      setThread(r.data.messages || []);
    } catch {
      setThread([]);
    }
  }, []);

  // initial load + poll conversation list
  useEffect(() => {
    setConversationsLoading(true);
    loadConversations().finally(() => setConversationsLoading(false));
    const id = setInterval(loadConversations, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [loadConversations]);

  // poll active thread
  useEffect(() => {
    if (!activePhone) return;
    loadThread(activePhone);
    const id = setInterval(() => loadThread(activePhone), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [activePhone, loadThread]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [thread]);

  // auto-resize the textarea as the draft grows/shrinks (multi-line support)
  useEffect(() => {
    const el = draftRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT) + "px";
  }, [draft]);

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file || !activePhone) return;

    setSending(true);
    setError("");

    // optimistic append
    const optimistic = {
      id: `temp-${Date.now()}`,
      phone: activePhone,
      direction: "outbound",
      body: file.name,
      status: "sending",
      msg_type: "document",
      media_filename: file.name,
      timestamp: new Date().toISOString(),
      read: true,
    };
    setThread((prev) => [...prev, optimistic]);

    try {
      const formData = new FormData();
      formData.append("phone", activePhone);
      formData.append("name", activeConversation?.name || "");
      formData.append("caption", "");
      formData.append("file", file);

      await API.post("/admin/messages/send-file", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      await loadThread(activePhone);
      loadConversations();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to send file");
      await loadThread(activePhone);
    }
    setSending(false);
  };

  const downloadMedia = async (message) => {
    try {
      const res = await API.get(`/admin/messages/media/${message.id}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = message.media_filename || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError("Failed to download attachment — it may have expired.");
    }
  };

  const openConversation = (phone) => {
    setActivePhone(phone);
    setThreadLoading(true);
    loadThread(phone).finally(() => setThreadLoading(false));
  };

  const startNewConversation = () => {
    const digits = newPhone.trim().replace(/[^\d]/g, "");
    if (digits.length < 10) {
      setError("Enter a valid phone number with country code, e.g. 919876543210");
      return;
    }
    setError("");
    setNewPhone("");
    openConversation(digits);
  };

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || !activePhone) return;
    setSending(true);
    setError("");

    // optimistic append
    const optimistic = {
      id: `temp-${Date.now()}`,
      phone: activePhone,
      direction: "outbound",
      body: text,
      status: "sending",
      timestamp: new Date().toISOString(),
      read: true,
    };
    setThread((prev) => [...prev, optimistic]);
    setDraft("");

    try {
      await sendWhatsappMessage({ phone: activePhone, message: text });
      await loadThread(activePhone);
      loadConversations();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to send message");
      await loadThread(activePhone);
    }
    setSending(false);
  };

  const activeConversation = conversations.find((c) => c.phone === activePhone);

  return (
    <div className="messages-tab">
      <div className="messages-layout">
        {/* Conversation list */}
        <div className="conv-panel">
          <div className="conv-panel-header">
            <span>Conversations</span>
          </div>

          <div className="conv-new-row">
            <input
              placeholder="New chat: phone with country code"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") startNewConversation(); }}
            />
            <button className="btn-ghost" onClick={startNewConversation}>Start</button>
          </div>

          <div className="conv-list">
            {conversationsLoading ? (
              <div className="spinner" />
            ) : conversations.length === 0 ? (
              <div className="empty-state-sm"><p>No conversations yet.</p></div>
            ) : (
              conversations.map((c) => (
                <button
                  key={c.phone}
                  className={`conv-row ${activePhone === c.phone ? "active" : ""}`}
                  onClick={() => openConversation(c.phone)}
                >
                  <div className="conv-row-top">
                    <span className="conv-row-name">{c.name || c.phone}</span>
                    <span className="conv-row-time">{formatTime(c.last_timestamp)}</span>
                  </div>
                  <div className="conv-row-bottom">
                    <span className="conv-row-preview">
                      {c.last_direction === "outbound" ? "You: " : ""}{c.last_message}
                    </span>
                    {c.unread_count > 0 && <span className="conv-unread-badge">{c.unread_count}</span>}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Thread panel */}
        <div className="thread-panel">
          {!activePhone ? (
            <div className="empty-state-sm thread-empty">
              <p>Select a conversation, or start a new one.</p>
            </div>
          ) : (
            <>
              <div className="thread-header">
                <span className="thread-header-name">{activeConversation?.name || activePhone}</span>
                <span className="thread-header-phone">{activePhone}</span>
              </div>

              <div className="thread-scroll" ref={scrollRef}>
                {threadLoading ? (
                  <div className="spinner" />
                ) : (
                  thread.map((m) => (
                    <div key={m.id} className={`bubble-row ${m.direction}`}>
                      <div className={`bubble ${m.direction}`}>
                        {m.media_id ? (
                          <button
                            type="button"
                            className="bubble-attachment"
                            onClick={() => downloadMedia(m)}
                          >
                            <span className="bubble-attachment-icon">📎</span>
                            <span className="bubble-attachment-name">
                              {m.media_filename || m.msg_type || "attachment"}
                            </span>
                            <span className="bubble-attachment-action">Download</span>
                          </button>
                        ) : m.media_filename ? (
                          // Outbound file you sent yourself - not re-downloadable
                          // from WhatsApp, just shown as a static attachment label.
                          <div className="bubble-attachment bubble-attachment-static">
                            <span className="bubble-attachment-icon">📎</span>
                            <span className="bubble-attachment-name">{m.media_filename}</span>
                          </div>
                        ) : (
                          <span className="bubble-body">{m.body}</span>
                        )}
                        <span className="bubble-meta">
                          {formatTime(m.timestamp)}
                          {m.direction === "outbound" && m.status === "failed" && (
                            <span className="bubble-failed"> · failed</span>
                          )}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {error && <div className="alert alert-error">{error}</div>}

              <div className="thread-input-row">
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileSelected}
                />
                <button
                  type="button"
                  className="btn-attach"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                  title="Attach a file"
                >
                  📎
                </button>
                <textarea
                  ref={draftRef}
                  className="thread-textarea"
                  placeholder="Type a message… (Shift+Enter for new line)"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (!sending) handleSend();
                    }
                    // Shift+Enter falls through and inserts a newline normally
                  }}
                  disabled={sending}
                  rows={1}
                />
                <button className="btn-primary" onClick={handleSend} disabled={sending || !draft.trim()}>
                  {sending ? "Sending…" : "Send"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}